import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Users,
  Trophy,
  Bot,
  Sparkles,
  MessageSquare,
  Heart,
  Share2,
  Search,
  Copy,
  Check,
  ChevronDown,
} from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'

type PromptItem = {
  title: string
  likes: number
  copies: number
  type: string
  content: string
}

type AgentItem = {
  title: string
  likes: number
  type: string
}

type Collaborator = {
  id: number
  name: string
  avatar: string
  score: number
  rank: number
  badge: string
  prompts: PromptItem[]
  agents: AgentItem[]
}
const collaborators: Collaborator[] = [
  {
    id: 1,
    name: 'Marina Santos',
    avatar: 'https://i.pravatar.cc/150?img=21',
    score: 4850,
    rank: 1,
    badge: 'Top Contributor',
    prompts: [
      {
        title: 'Análise de Sentimentos Avançada',
        likes: 234,
        copies: 89,
        type: 'prompt',
        content:
          'Você é um especialista em análise de sentimentos. Analise o texto fornecido e identifique:\n\n1. Sentimento geral (positivo, negativo, neutro)\n2. Emoções específicas detectadas\n3. Intensidade emocional (1-10)\n4. Palavras-chave que indicam o sentimento\n5. Contexto e nuances\n\nForneça uma análise detalhada e estruturada.',
      },
      {
        title: 'Gerador de Relatórios Financeiros',
        likes: 189,
        copies: 67,
        type: 'prompt',
        content:
          'Atue como um analista financeiro sênior. Com base nos dados fornecidos, gere um relatório financeiro completo incluindo:\n\n- Resumo executivo\n- Análise de receitas e despesas\n- Indicadores de performance (KPIs)\n- Projeções e tendências\n- Recomendações estratégicas\n\nUse linguagem profissional e formatação clara.',
      },
    ],
    agents: [{ title: 'Assistente de Vendas B2B', likes: 312, type: 'agent' }],
  },
  {
    id: 8,
    name: 'Lucas Mendes',
    avatar: 'https://i.pravatar.cc/150?img=12',
    score: 4500,
    rank: 2,
    badge: 'Tech Enthusiast',
    prompts: [
      {
        title: 'Debugger de Código Inteligente',
        likes: 189,
        copies: 71,
        type: 'prompt',
        content:
          'Analise o código fornecido e identifique:\n\n1. Bugs e erros potenciais\n2. Problemas de performance\n3. Vulnerabilidades de segurança\n4. Melhorias de legibilidade\n5. Boas práticas não seguidas\n\nForneça correções com explicações detalhadas.',
      },
    ],
    agents: [{ title: 'Assistente de Code Review', likes: 212, type: 'agent' }],
  },
  {
    id: 7,
    name: 'Ana Paula Ferreira',
    avatar: 'https://i.pravatar.cc/150?img=16',
    score: 4280,
    rank: 3,
    badge: 'UX Designer',
    prompts: [
      {
        title: 'Análise de Usabilidade de Interface',
        likes: 245,
        copies: 78,
        type: 'prompt',
        content:
          'Atue como especialista em UX/UI Design. Analise a interface descrita e forneça:\n\n1. Pontos fortes da usabilidade\n2. Problemas de experiência do usuário\n3. Sugestões de melhorias visuais\n4. Recomendações de acessibilidade\n5. Fluxo de navegação otimizado\n\nBaseie-se nas heurísticas de Nielsen e boas práticas de design.',
      },
      {
        title: 'Gerador de Design System',
        likes: 198,
        copies: 65,
        type: 'prompt',
        content:
          'Crie um Design System completo incluindo:\n\n1. Paleta de cores (primárias, secundárias, neutras)\n2. Tipografia (fontes, tamanhos, pesos)\n3. Espaçamentos e grid\n4. Componentes básicos (botões, inputs, cards)\n5. Estados de interação (hover, focus, disabled)\n\nForneça tokens CSS e diretrizes de uso.',
      },
    ],
    agents: [],
  },
  {
    id: 2,
    name: 'Ricardo Oliveira',
    avatar: 'https://i.pravatar.cc/150?img=3',
    score: 4100,
    rank: 4,
    badge: 'AI Expert',
    prompts: [
      {
        title: 'Otimizador de Copy para Ads',
        likes: 178,
        copies: 52,
        type: 'prompt',
        content:
          'Você é um copywriter especialista em anúncios digitais. Otimize o texto fornecido para:\n\n1. Aumentar taxa de cliques (CTR)\n2. Gerar urgência e escassez\n3. Incluir call-to-action poderoso\n4. Adaptar para diferentes plataformas (Facebook, Google, Instagram)\n5. Manter limite de caracteres\n\nForneça 3 variações otimizadas.',
      },
    ],
    agents: [
      { title: 'Bot de Atendimento ao Cliente', likes: 256, type: 'agent' },
      { title: 'Analisador de Contratos', likes: 198, type: 'agent' },
    ],
  },
  {
    id: 3,
    name: 'Juliana Costa',
    avatar: 'https://i.pravatar.cc/150?img=5',
    score: 3980,
    rank: 5,
    badge: 'Prompt Master',
    prompts: [
      {
        title: 'Criador de Personas de Marketing',
        likes: 145,
        copies: 38,
        type: 'prompt',
        content:
          'Crie uma persona de marketing detalhada com:\n\n- Nome e idade\n- Profissão e renda\n- Dores e desafios\n- Objetivos e aspirações\n- Canais de comunicação preferidos\n- Objeções comuns\n- Gatilhos de compra\n\nBaseie-se no produto/serviço descrito.',
      },
      {
        title: 'Tradutor Técnico Multilíngue',
        likes: 167,
        copies: 45,
        type: 'prompt',
        content:
          'Atue como tradutor técnico especializado. Traduza o conteúdo mantendo:\n\n1. Precisão terminológica\n2. Contexto cultural\n3. Tom e registro adequados\n4. Formatação original\n\nIdiomas suportados: PT, EN, ES, FR, DE.',
      },
      {
        title: 'Resumidor de Documentos Legais',
        likes: 134,
        copies: 31,
        type: 'prompt',
        content:
          'Resuma documentos legais de forma clara e acessível:\n\n1. Pontos principais do documento\n2. Obrigações das partes\n3. Prazos importantes\n4. Riscos e penalidades\n5. Termos técnicos explicados\n\nUse linguagem simples sem perder a precisão jurídica.',
      },
    ],
    agents: [],
  },
  {
    id: 4,
    name: 'Fernando Lima',
    avatar: 'https://i.pravatar.cc/150?img=8',
    score: 3650,
    rank: 6,
    badge: 'QA Specialist',
    prompts: [
      {
        title: 'Gerador de Casos de Teste',
        likes: 156,
        copies: 42,
        type: 'prompt',
        content:
          'Atue como QA Engineer sênior. Gere casos de teste completos para a funcionalidade descrita:\n\n1. Cenários positivos (happy path)\n2. Cenários negativos e edge cases\n3. Testes de boundary values\n4. Testes de integração\n5. Critérios de aceite\n\nFormate em Gherkin (Given/When/Then) quando aplicável.',
      },
      {
        title: 'Análise de Bugs e Root Cause',
        likes: 134,
        copies: 38,
        type: 'prompt',
        content:
          'Analise o bug reportado e forneça:\n\n1. Classificação de severidade e prioridade\n2. Passos para reprodução detalhados\n3. Análise de root cause\n4. Impacto no usuário e no sistema\n5. Sugestões de correção\n6. Testes de regressão necessários\n\nUse formato estruturado para documentação.',
      },
    ],
    agents: [{ title: 'Assistente de Automação de Testes', likes: 198, type: 'agent' }],
  },
  {
    id: 5,
    name: 'Camila Rodrigues',
    avatar: 'https://i.pravatar.cc/150?img=9',
    score: 3420,
    rank: 7,
    badge: 'Community Builder',
    prompts: [
      {
        title: 'Criador de E-mails Persuasivos',
        likes: 156,
        copies: 42,
        type: 'prompt',
        content:
          'Crie e-mails persuasivos que convertem:\n\n1. Assunto irresistível (max 50 caracteres)\n2. Abertura que prende atenção\n3. Corpo com benefícios claros\n4. Prova social quando aplicável\n5. CTA único e claro\n\nAdapte o tom conforme o objetivo: vendas, nutrição ou reativação.',
      },
      {
        title: 'Planejador de Conteúdo Mensal',
        likes: 112,
        copies: 25,
        type: 'prompt',
        content:
          'Crie um calendário de conteúdo mensal com:\n\n- 4 posts por semana\n- Mix de formatos (carrossel, reels, stories)\n- Temas alinhados aos objetivos\n- Melhores horários de postagem\n- Hashtags estratégicas\n- Ideias de legendas\n\nConsidere datas comemorativas e tendências.',
      },
    ],
    agents: [{ title: 'Assistente de RH', likes: 145, type: 'agent' }],
  },
  {
    id: 6,
    name: 'Bruno Almeida',
    avatar: 'https://i.pravatar.cc/150?img=11',
    score: 3180,
    rank: 8,
    badge: 'Innovator',
    prompts: [
      {
        title: 'Analisador de Dados de Vendas',
        likes: 98,
        copies: 19,
        type: 'prompt',
        content:
          'Analise os dados de vendas fornecidos e identifique:\n\n1. Tendências de crescimento/queda\n2. Produtos mais rentáveis\n3. Sazonalidades\n4. Oportunidades de cross-sell\n5. Previsões para próximo período\n\nApresente insights acionáveis com visualizações sugeridas.',
      },
    ],
    agents: [
      { title: 'Bot de Qualificação de Leads', likes: 167, type: 'agent' },
      { title: 'Gerador de Propostas Comerciais', likes: 134, type: 'agent' },
    ],
  },
]

const SymphonyHub = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCollaborator, setSelectedCollaborator] = useState<Collaborator | null>(null)
  const [selectedPrompt, setSelectedPrompt] = useState<(PromptItem & { author: string }) | null>(
    null
  )
  const [promptLikes, setPromptLikes] = useState<Record<string, number>>({})
  const [promptCopies, setPromptCopies] = useState<Record<string, number>>({})
  const [copied, setCopied] = useState(false)
  const [pointsAnimation, setPointsAnimation] = useState<{ show: boolean; key: string }>({
    show: false,
    key: '',
  })
  const [userPointsAnimation, setUserPointsAnimation] = useState<{ show: boolean; points: number }>(
    { show: false, points: 0 }
  )
  const [collaboratorScores, setCollaboratorScores] = useState<Record<number, number>>({})
  const { toast } = useToast()

  const filteredCollaborators = collaborators.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.prompts.some((p) => p.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      c.agents.some((a) => a.title.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'text-yellow-500'
      case 2:
        return 'text-gray-400'
      case 3:
        return 'text-amber-600'
      default:
        return 'text-muted-foreground'
    }
  }

  const getCollaboratorScore = (collaboratorId: number, originalScore: number) => {
    return collaboratorScores[collaboratorId] ?? originalScore
  }

  const triggerPointsAnimation = (
    key: string,
    collaboratorId: number,
    originalScore: number,
    points: number = 10
  ) => {
    setPointsAnimation({ show: true, key })
    setCollaboratorScores((prev) => ({
      ...prev,
      [collaboratorId]: (prev[collaboratorId] ?? originalScore) + points,
    }))
    setTimeout(() => setPointsAnimation({ show: false, key: '' }), 1500)
  }

  const handleCopyPrompt = async (content: string, promptTitle: string, originalCopies: number) => {
    await navigator.clipboard.writeText(content)
    setCopied(true)

    // Increment copy count
    setPromptCopies((prev) => ({
      ...prev,
      [promptTitle]: (prev[promptTitle] ?? originalCopies) + 1,
    }))

    // Owner gets +10 points
    if (selectedCollaborator) {
      triggerPointsAnimation(
        `copy-${promptTitle}`,
        selectedCollaborator.id,
        selectedCollaborator.score,
        10
      )
    }

    // User who copied gets +5 points (show animation)
    setUserPointsAnimation({ show: true, points: 5 })
    setTimeout(() => setUserPointsAnimation({ show: false, points: 0 }), 1500)

    toast({
      title: 'Prompt copiado! 📋',
      description: selectedCollaborator
        ? `+10 pontos para ${selectedCollaborator.name} e +5 pontos para você!`
        : 'O prompt foi copiado. +5 pontos para você!',
    })
    setTimeout(() => setCopied(false), 2000)
  }

  const getPromptCopies = (promptTitle: string, originalCopies: number) => {
    return promptCopies[promptTitle] ?? originalCopies
  }

  const handleLikePrompt = (promptTitle: string, currentLikes: number) => {
    const key = promptTitle
    const hasLiked = promptLikes[key] !== undefined
    if (!hasLiked) {
      setPromptLikes((prev) => ({ ...prev, [key]: currentLikes + 1 }))

      if (selectedCollaborator) {
        triggerPointsAnimation(
          `like-${promptTitle}`,
          selectedCollaborator.id,
          selectedCollaborator.score
        )
      }

      toast({
        title: 'Obrigado pelo like! ❤️',
        description: selectedCollaborator
          ? `+10 pontos para ${selectedCollaborator.name}!`
          : 'Você curtiu este prompt.',
      })
    }
  }

  const getPromptLikes = (promptTitle: string, originalLikes: number) => {
    return promptLikes[promptTitle] ?? originalLikes
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 container py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="w-10 h-10 text-primary" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Sym! <span className="text-primary">Hub</span>
            </h1>
          </div>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Conheça nossa comunidade de inteligentes. Explore prompts e agentes criados por pessoas
            com inteligência aumentada como você.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <Card className="text-center p-4">
            <div className="text-3xl font-bold text-primary">248</div>
            <div className="text-sm text-muted-foreground">Colaboradores</div>
          </Card>
          <Card className="text-center p-4">
            <div className="text-3xl font-bold text-primary">1.2K</div>
            <div className="text-sm text-muted-foreground">Prompts Criados</div>
          </Card>
          <Card className="text-center p-4">
            <div className="text-3xl font-bold text-primary">456</div>
            <div className="text-sm text-muted-foreground">Agentes Ativos</div>
          </Card>
          <Card className="text-center p-4">
            <div className="text-3xl font-bold text-primary">15.8K</div>
            <div className="text-sm text-muted-foreground">Total de Likes</div>
          </Card>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative mb-8"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar colaboradores, prompts ou agentes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="ranking" className="space-y-6">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="ranking" className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Ranking
            </TabsTrigger>
            <TabsTrigger value="creations" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Criações
            </TabsTrigger>
          </TabsList>

          {/* Ranking Tab */}
          <TabsContent value="ranking">
            <div className="grid gap-4">
              {filteredCollaborators.map((collaborator, index) => (
                <motion.div
                  key={collaborator.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className="hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedCollaborator(collaborator)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Rank */}
                        <div
                          className={`text-2xl font-bold w-8 ${getRankColor(collaborator.rank)}`}
                        >
                          #{collaborator.rank}
                        </div>

                        {/* Avatar */}
                        <Avatar className="w-14 h-14 border-2 border-primary/30">
                          <AvatarImage src={collaborator.avatar} alt={collaborator.name} />
                          <AvatarFallback>{collaborator.name.charAt(0)}</AvatarFallback>
                        </Avatar>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg">{collaborator.name}</h3>
                            <Badge variant="secondary" className="text-xs">
                              {collaborator.badge}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              {collaborator.prompts.length} prompts
                            </span>
                            <span className="flex items-center gap-1">
                              <Bot className="w-4 h-4" />
                              {collaborator.agents.length} agentes
                            </span>
                          </div>
                        </div>

                        {/* Score */}
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">
                            {collaborator.score.toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground">pontos</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Creations Tab */}
          <TabsContent value="creations">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCollaborators
                .flatMap((collaborator) => [
                  ...collaborator.prompts.map((prompt, idx) => ({
                    ...prompt,
                    author: collaborator.name,
                    avatar: collaborator.avatar,
                    id: `${collaborator.id}-prompt-${idx}`,
                  })),
                  ...collaborator.agents.map((agent, idx) => ({
                    ...agent,
                    author: collaborator.name,
                    avatar: collaborator.avatar,
                    id: `${collaborator.id}-agent-${idx}`,
                  })),
                ])
                .sort((a, b) => b.likes - a.likes)
                .map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <Card className="hover:border-primary/50 transition-colors h-full">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge
                            variant={item.type === 'agent' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {item.type === 'agent' ? (
                              <>
                                <Bot className="w-3 h-3 mr-1" /> Agente
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3 mr-1" /> Prompt
                              </>
                            )}
                          </Badge>
                        </div>
                        <h3 className="font-semibold line-clamp-2">{item.title}</h3>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={item.avatar} alt={item.author} />
                              <AvatarFallback>{item.author.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-muted-foreground">{item.author}</span>
                          </div>
                          <div className="flex items-center gap-3 text-muted-foreground">
                            <button className="flex items-center gap-1 hover:text-primary transition-colors">
                              <Heart className="w-4 h-4" />
                              <span className="text-sm">{item.likes}</span>
                            </button>
                            <button className="hover:text-primary transition-colors">
                              <Share2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Collaborator Profile Dialog */}
        <Dialog
          open={!!selectedCollaborator}
          onOpenChange={() => {
            setSelectedCollaborator(null)
            setSelectedPrompt(null)
          }}
        >
          <DialogContent className="max-w-lg max-h-[90vh]">
            {selectedCollaborator && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-4">
                    <Avatar className="w-20 h-20 border-2 border-primary/30">
                      <AvatarImage
                        src={selectedCollaborator.avatar}
                        alt={selectedCollaborator.name}
                      />
                      <AvatarFallback className="text-2xl">
                        {selectedCollaborator.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <DialogTitle className="text-2xl">{selectedCollaborator.name}</DialogTitle>
                      <Badge variant="secondary" className="mt-2">
                        {selectedCollaborator.badge}
                      </Badge>
                      <div className="flex items-center gap-2 mt-2 relative">
                        <Trophy className="w-4 h-4 text-primary" />
                        <span className="text-primary font-semibold">
                          {getCollaboratorScore(
                            selectedCollaborator.id,
                            selectedCollaborator.score
                          ).toLocaleString()}{' '}
                          pontos
                        </span>
                        <span className="text-muted-foreground">
                          • Rank #{selectedCollaborator.rank}
                        </span>
                        {pointsAnimation.show && (
                          <motion.span
                            initial={{ opacity: 1, y: 0 }}
                            animate={{ opacity: 0, y: -20 }}
                            transition={{ duration: 1.5 }}
                            className="absolute -top-4 left-16 text-green-500 font-bold text-sm"
                          >
                            +10
                          </motion.span>
                        )}
                      </div>
                    </div>
                  </div>
                </DialogHeader>

                <ScrollArea className="max-h-[50vh] pr-4">
                  <div className="space-y-6 mt-4">
                    {/* Prompts */}
                    {selectedCollaborator.prompts.length > 0 && (
                      <div>
                        <h4 className="font-semibold flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-primary" />
                          Prompts ({selectedCollaborator.prompts.length})
                        </h4>
                        <div className="space-y-2">
                          {selectedCollaborator.prompts.map((prompt, idx) => {
                            const isExpanded = selectedPrompt?.title === prompt.title
                            return (
                              <Card
                                key={idx}
                                className={`transition-all duration-200 ${isExpanded ? 'border-primary' : 'hover:border-primary/50'}`}
                              >
                                <div
                                  className="p-3 cursor-pointer"
                                  onClick={() =>
                                    setSelectedPrompt(
                                      isExpanded
                                        ? null
                                        : { ...prompt, author: selectedCollaborator.name }
                                    )
                                  }
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{prompt.title}</span>
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center gap-1 text-muted-foreground">
                                        <Heart className="w-4 h-4" />
                                        <span className="text-sm">
                                          {getPromptLikes(prompt.title, prompt.likes)}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1 text-muted-foreground">
                                        <Copy className="w-4 h-4" />
                                        <span className="text-sm">
                                          {getPromptCopies(prompt.title, prompt.copies)}
                                        </span>
                                      </div>
                                      <ChevronDown
                                        className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                      />
                                    </div>
                                  </div>
                                </div>

                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="px-3 pb-3"
                                  >
                                    <div className="bg-muted/50 rounded-lg p-3 font-mono text-sm whitespace-pre-wrap max-h-40 overflow-y-auto mb-3">
                                      {prompt.content}
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <Button
                                        variant={promptLikes[prompt.title] ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleLikePrompt(prompt.title, prompt.likes)
                                        }}
                                        disabled={!!promptLikes[prompt.title]}
                                      >
                                        <Heart
                                          className={`w-4 h-4 mr-2 ${promptLikes[prompt.title] ? 'fill-current' : ''}`}
                                        />
                                        Curtir
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleCopyPrompt(
                                            prompt.content,
                                            prompt.title,
                                            prompt.copies
                                          )
                                        }}
                                      >
                                        {copied ? (
                                          <>
                                            <Check className="w-4 h-4 mr-2" />
                                            Copiado!
                                          </>
                                        ) : (
                                          <>
                                            <Copy className="w-4 h-4 mr-2" />
                                            Copiar
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  </motion.div>
                                )}
                              </Card>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Agents */}
                    {selectedCollaborator.agents.length > 0 && (
                      <div>
                        <h4 className="font-semibold flex items-center gap-2 mb-3">
                          <Bot className="w-4 h-4 text-primary" />
                          Agentes ({selectedCollaborator.agents.length})
                        </h4>
                        <div className="space-y-2">
                          {selectedCollaborator.agents.map((agent, idx) => (
                            <Card
                              key={idx}
                              className="p-3 cursor-pointer hover:border-primary/50 transition-colors"
                              onClick={() => window.open('https://symphony.fcamara.com/', '_blank')}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{agent.title}</span>
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Heart className="w-4 h-4" />
                                  <span className="text-sm">{agent.likes}</span>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCollaborator.prompts.length === 0 &&
                      selectedCollaborator.agents.length === 0 && (
                        <p className="text-muted-foreground text-center py-4">
                          Este colaborador ainda não possui criações.
                        </p>
                      )}
                  </div>
                </ScrollArea>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>

      <Footer />
    </div>
  )
}

export default SymphonyHub
